import { useEffect, useMemo, useRef, useState } from 'react';
import { ActionIcon, Box, Group, Image as MantineImage, Loader, Text } from '@mantine/core';
import { IconExternalLink, IconX } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as modelViewer from '@google/model-viewer';
import { clientRoutes } from '@/ApiPaths';
import { getProductById, isProductLockedResponse } from '@/Api';
import {
  AnnotationHotspot,
  AnnotationImageModal,
  AnnotationInfoBox,
  AnnotationNavigator,
  AnnotationProductModal,
  useAnnotationBarPortalTargetStyle,
  type AnnotationNavigatorRef,
} from '@/components/Annotations';
import { AnimationSelector } from '@/components/AnimationSelector/AnimationSelector';
import { ModelViewerResetZoomButton } from '@/components/ModelViewer/ModelViewerResetZoomButton';
import { ViewerPlayPauseButton } from '@/components/ModelViewer/ViewerControlButton';
import { ProductListPreviewPicture } from '@/components/ProductListPreviewPicture';
import { PRODUCT_CARD_PREVIEW_BG } from '@/constants/productPreview';
import {
  DEFAULT_MODEL_VIEWER_PRESENTATION,
  presentationSettingsToAttrs,
  presentationSettingsToViewerStyle,
  resolvePresentationSettings,
  type ModelViewerPresentationSettings,
} from '@/constants/modelViewerPresentation';
import { useAnnotationClick } from '@/hooks/useAnnotationClick';
import { useAnnotationClickOutside } from '@/hooks/useAnnotationClickOutside';
import { useAnnotationPositionUpdate } from '@/hooks/useAnnotationPositionUpdate';
import { useModelViewerDynamicZoom } from '@/hooks/useModelViewerDynamicZoom';
import { MODEL_VIEWER_RELAXED_ZOOM_ATTRS } from '@/utils/annotationConstants';
import { colorWithAlpha } from '@/utils/colorWithAlpha';
import { glbZoomSrc, productGlbUrl, productUsdzUrl } from '@/utils/productModelUrl';
import type { Annotation, ModelViewerSettings } from '@/types/Annotation.model';
import type { IProduct } from '@/types/Product.model';
import classes from './CompareProductPanel.module.css';

void modelViewer;

type CompareProductPanelProps = {
  productId: string;
  fallback?: IProduct | null;
  accentColor: string;
  onRemove: () => void;
};

export function CompareProductPanel({
  productId,
  fallback,
  accentColor,
  onRemove,
}: CompareProductPanelProps) {
  const { t } = useTranslation();
  const modelViewerRef = useRef<modelViewer.ModelViewerElement | null>(null);
  const annotationNavigatorRef = useRef<AnnotationNavigatorRef>(null);
  const descriptionBoxRef = useRef<HTMLDivElement>(null);
  const annotationBarId = `compare-annotation-bar-${productId}`;
  const annotationBarPortalStyle = useAnnotationBarPortalTargetStyle({ zIndex: 12 });

  const [product, setProduct] = useState<IProduct | null>(fallback ?? null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [cameraOrbit, setCameraOrbit] = useState('');
  const [cameraTarget, setCameraTarget] = useState('');
  const [cameraFov, setCameraFov] = useState('');
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [annotationsVisible, setAnnotationsVisible] = useState(true);
  const [activeAnnotation, setActiveAnnotation] = useState<Annotation | null>(null);
  const [annotationPosition, setAnnotationPosition] = useState<{ x: number; y: number } | null>(
    null
  );
  const [presentationSettings, setPresentationSettings] =
    useState<ModelViewerPresentationSettings>(DEFAULT_MODEL_VIEWER_PRESENTATION);
  const [hasAnimations, setHasAnimations] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [imageModalOpened, setImageModalOpened] = useState(false);
  const [productModalOpened, setProductModalOpened] = useState(false);
  const [selectedAnnotationProduct, setSelectedAnnotationProduct] = useState<IProduct | null>(
    null
  );

  const glbSrc = glbZoomSrc(product, product?._id);
  useModelViewerDynamicZoom(modelViewerRef, glbSrc);

  const presentationAttrs = useMemo(
    () => presentationSettingsToAttrs(presentationSettings),
    [presentationSettings]
  );
  const presentationViewerStyle = useMemo(
    () => presentationSettingsToViewerStyle(presentationSettings),
    [presentationSettings]
  );

  const closeAnnotationUi = () => {
    setActiveAnnotation(null);
    setAnnotationPosition(null);
    setImageModalOpened(false);
    setProductModalOpened(false);
    setSelectedAnnotationProduct(null);
  };

  useAnnotationClickOutside({
    descriptionBoxRef,
    activeAnnotation,
    onClose: closeAnnotationUi,
    imageModalOpened,
    productModalOpened,
  });

  const handleAnnotationClick = useAnnotationClick({
    modelViewerRef,
    annotationNavigatorRef,
    setActiveAnnotation,
    setAnnotationPosition,
    onCloseModals: () => {
      setImageModalOpened(false);
      setProductModalOpened(false);
      setSelectedAnnotationProduct(null);
    },
  });

  useAnnotationPositionUpdate({
    modelViewerRef,
    activeAnnotation,
    setAnnotationPosition,
  });

  useEffect(() => {
    let cancelled = false;
    setLoadFailed(false);
    setLoading(true);
    setProduct(fallback ?? null);
    setAnnotations([]);
    setActiveAnnotation(null);
    setAnnotationPosition(null);
    setHasAnimations(false);
    setIsPlaying(true);
    setPresentationSettings(DEFAULT_MODEL_VIEWER_PRESENTATION);

    getProductById(productId)
      .then((res) => {
        if (cancelled) return;
        if (isProductLockedResponse(res)) {
          setLoadFailed(true);
          return;
        }
        setProduct(res);
        if (res.modelViewerSettings) {
          try {
            const settings: ModelViewerSettings = JSON.parse(res.modelViewerSettings);
            setCameraOrbit(settings.initialCameraOrbit ?? '');
            setCameraTarget(settings.initialCameraTarget ?? '');
            setCameraFov(settings.initialCameraFieldOfView ?? '');
            setAnnotations(Array.isArray(settings.annotations) ? settings.annotations : []);
            setPresentationSettings(resolvePresentationSettings(settings));
          } catch {
            setCameraOrbit('');
            setCameraTarget('');
            setCameraFov('');
            setAnnotations([]);
            setPresentationSettings(DEFAULT_MODEL_VIEWER_PRESENTATION);
          }
        } else {
          setCameraOrbit('');
          setCameraTarget('');
          setCameraFov('');
          setAnnotations([]);
          setPresentationSettings(DEFAULT_MODEL_VIEWER_PRESENTATION);
        }
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fallback is decorative until fetch completes
  }, [productId]);

  useEffect(() => {
    const mv = modelViewerRef.current;
    if (!mv || !glbSrc) {
      setHasAnimations(false);
      return;
    }

    const checkAnimations = () => {
      try {
        const list = (mv as modelViewer.ModelViewerElement & { availableAnimations?: string[] })
          .availableAnimations;
        const valid = (list || []).filter((name) => name && name.trim() !== '');
        setHasAnimations(valid.length > 0);
      } catch {
        setHasAnimations(false);
      }
    };

    const onLoad = () => {
      window.setTimeout(checkAnimations, 100);
    };

    mv.addEventListener('load', onLoad);
    const interval = window.setInterval(checkAnimations, 500);
    checkAnimations();

    return () => {
      mv.removeEventListener('load', onLoad);
      window.clearInterval(interval);
    };
  }, [glbSrc, productId]);

  const hasGlb = Boolean(product?.availableFormats?.includes('.glb') && product._id);
  const displayName = product?.productName || fallback?.productName || productId;

  const cameraAttrs = useMemo(
    () => ({
      ...(cameraOrbit ? { 'camera-orbit': cameraOrbit } : {}),
      ...(cameraTarget ? { 'camera-target': cameraTarget } : {}),
      ...(cameraFov ? { 'field-of-view': cameraFov } : {}),
    }),
    [cameraOrbit, cameraTarget, cameraFov]
  );

  const toggleAnimation = () => {
    if (isPlaying) modelViewerRef.current?.pause();
    else modelViewerRef.current?.play();
    setIsPlaying((prev) => !prev);
  };

  return (
    <Box className={classes.panel} style={{ borderColor: colorWithAlpha(accentColor, 0.28) }}>
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs" mb="sm">
        <div className={classes.meta}>
          <Text fw={700} lineClamp={1}>
            {displayName}
          </Text>
          {product?.articleNumber ? (
            <Text size="xs" c="dimmed" lineClamp={1}>
              {product.articleNumber}
            </Text>
          ) : null}
        </div>
        <Group gap={4} wrap="nowrap">
          <ActionIcon
            component={Link}
            to={clientRoutes.product.replace(':id', productId)}
            variant="subtle"
            color={accentColor}
            aria-label={t('marketplace.compare.openProduct')}
          >
            <IconExternalLink size={16} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            color="red"
            onClick={onRemove}
            aria-label={t('marketplace.compare.remove')}
          >
            <IconX size={16} />
          </ActionIcon>
        </Group>
      </Group>

      <Box className={classes.viewerShell} style={{ background: PRODUCT_CARD_PREVIEW_BG }}>
        {loading ? (
          <Box className={classes.placeholder}>
            <Loader size="sm" color={accentColor} />
          </Box>
        ) : hasGlb && product ? (
          <>
            <ModelViewerResetZoomButton
              modelViewerRef={modelViewerRef}
              cameraDefaults={{
                cameraOrbit,
                cameraTarget,
                cameraFieldOfView: cameraFov,
              }}
            />
            <model-viewer
              key={product._id}
              ref={modelViewerRef}
              className={classes.viewer}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                display: 'block',
                overflow: 'hidden',
                ...presentationViewerStyle,
              }}
              src={productGlbUrl(product._id)}
              ios-src={
                product.availableFormats.includes('.usdz') ? productUsdzUrl(product._id) : ''
              }
              interaction-prompt="auto"
              interaction-prompt-style="basic"
              controls="play pause"
              {...presentationAttrs}
              camera-controls
              {...MODEL_VIEWER_RELAXED_ZOOM_ATTRS}
              touch-action="pan-y"
              enable-pan
              autoplay
              {...cameraAttrs}
            >
              {annotationsVisible &&
                annotations.map((annotation) => (
                  <AnnotationHotspot
                    key={annotation.id}
                    annotation={annotation}
                    onClick={handleAnnotationClick}
                  />
                ))}
            </model-viewer>

            <Box id={annotationBarId} style={annotationBarPortalStyle} />

            <Box className={classes.controls}>
              <AnnotationNavigator
                ref={annotationNavigatorRef}
                annotations={annotations}
                modelViewerRef={modelViewerRef}
                color={accentColor}
                onAnnotationChange={setActiveAnnotation}
                onAnnotationsVisibleChange={setAnnotationsVisible}
                hasAnimationSelector={hasAnimations}
                inline
                barPortalTargetId={annotationBarId}
              />
              {hasAnimations ? (
                <Box style={{ position: 'relative', zIndex: 12 }}>
                  <AnimationSelector modelViewerRef={modelViewerRef} color={accentColor} />
                </Box>
              ) : null}
              {hasAnimations ? (
                <ViewerPlayPauseButton
                  accentColor={accentColor}
                  isPlaying={isPlaying}
                  onClick={toggleAnimation}
                />
              ) : null}
            </Box>

            {activeAnnotation && annotationPosition ? (
              <AnnotationInfoBox
                activeAnnotation={activeAnnotation}
                annotationPosition={annotationPosition}
                descriptionBoxRef={descriptionBoxRef}
                backgroundColor={accentColor}
                onProductClick={(linked) => {
                  setSelectedAnnotationProduct(linked);
                  setProductModalOpened(true);
                }}
                onImageClick={() => setImageModalOpened(true)}
              />
            ) : null}

            <AnnotationImageModal
              opened={imageModalOpened}
              onClose={() => setImageModalOpened(false)}
              annotation={activeAnnotation}
              ImageComponent={MantineImage}
            />
            <AnnotationProductModal
              opened={productModalOpened}
              onClose={() => {
                setProductModalOpened(false);
                setSelectedAnnotationProduct(null);
              }}
              product={selectedAnnotationProduct}
            />
          </>
        ) : (
          <Box className={classes.placeholder}>
            {(product || fallback)?.productImage && !loadFailed ? (
              <ProductListPreviewPicture
                entityId={productId}
                productImage={(product || fallback)?.productImage}
                imageVersion={(product || fallback)?.imageVersion}
                alt={displayName}
              />
            ) : (
              <Text size="sm" c="dimmed" ta="center">
                {t('marketplace.compare.noGlb')}
              </Text>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
